import { createRouter, createWebHistory } from "vue-router";
import { useTabsStore } from "../store/tabs";
import { useUserStore } from "../store/user";
import {
  buildMenuRoutes,
  hasAccessibleMenuPath,
  normalizeMenuPath,
} from "./menuRoutes";

const LOGIN_PATH = "/login";
const ROOT_PATH = "/";
const ROOT_ROUTE_NAME = "AdminLayout";
const NOT_FOUND_ROUTE_NAME = "AuthorizedNotFound";

const routes = [
  {
    path: LOGIN_PATH,
    name: "Login",
    component: () => import("../views/login/index.vue"),
    meta: {
      guest: true,
      hideTab: true,
    },
  },
  {
    path: ROOT_PATH,
    name: ROOT_ROUTE_NAME,
    component: () => import("../layout/index.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: NOT_FOUND_ROUTE_NAME,
    component: () => import("../layout/index.vue"),
    meta: {
      requiresAuth: true,
      hideTab: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

let dynamicRouteNames = new Set();
let dynamicRouteSignature = "";
let handlingUnauthorized = false;

const createRouteSignature = (menuRoutes) =>
  JSON.stringify(
    menuRoutes.map((route) => ({
      path: route.path,
      name: route.name,
      title: route.meta?.title,
      icon: route.meta?.icon,
      menuId: route.meta?.menuId,
    })),
  );

const resetDynamicMenuRoutes = () => {
  dynamicRouteNames.forEach((routeName) => {
    if (router.hasRoute(routeName)) {
      router.removeRoute(routeName);
    }
  });

  dynamicRouteNames = new Set();
  dynamicRouteSignature = "";
};

const syncDynamicMenuRoutes = (menus = [], permissions = []) => {
  const menuRoutes = buildMenuRoutes(menus, permissions);
  const nextSignature = createRouteSignature(menuRoutes);

  if (nextSignature === dynamicRouteSignature) {
    return false;
  }

  resetDynamicMenuRoutes();

  menuRoutes.forEach((route) => {
    router.addRoute(ROOT_ROUTE_NAME, route);
    dynamicRouteNames.add(route.name);
  });

  dynamicRouteSignature = nextSignature;

  return true;
};

const isNotFoundMatch = (to) =>
  to.matched.some((routeRecord) => routeRecord.name === NOT_FOUND_ROUTE_NAME);

const hasRouteAccess = (route, menus, userStore) => {
  const menuPaths = route.meta.menuPaths || [route.meta.menuPath || route.path];
  const hasMenuAccess = menuPaths.some((path) =>
    hasAccessibleMenuPath(menus, path),
  );
  const hasPermissionAccess = (route.meta.permissionsAll || []).every(
    (permission) => userStore.hasPermission(permission),
  );

  return hasMenuAccess && hasPermissionAccess;
};

const getSafeRedirect = (redirect, fallback, menus = [], userStore) => {
  if (
    typeof redirect !== "string" ||
    !redirect.startsWith("/") ||
    redirect.startsWith("//") ||
    normalizeMenuPath(redirect) === LOGIN_PATH
  ) {
    return fallback;
  }

  const resolved = router.resolve(redirect);
  return hasRouteAccess(resolved, menus, userStore) ? redirect : fallback;
};

router.beforeEach(async (to) => {
  const userStore = useUserStore();

  if (to.meta.guest) {
    if (!userStore.loggedIn) {
      resetDynamicMenuRoutes();
      return true;
    }

    syncDynamicMenuRoutes(userStore.menus, userStore.permissions);

    const fallbackPath = userStore.defaultMenuPath;

    if (!fallbackPath) {
      return true;
    }

    return getSafeRedirect(
      to.query.redirect,
      fallbackPath,
      userStore.menus,
      userStore,
    );
  }

  if (!to.meta.requiresAuth) {
    return true;
  }

  if (!userStore.loggedIn) {
    resetDynamicMenuRoutes();

    const hasSession = await userStore.restoreSession();

    if (!hasSession) {
      return {
        path: LOGIN_PATH,
        query: {
          redirect: to.fullPath,
        },
      };
    }
  }

  const routesChanged = syncDynamicMenuRoutes(
    userStore.menus,
    userStore.permissions,
  );
  const fallbackPath = userStore.defaultMenuPath;

  if (!fallbackPath) {
    return {
      path: LOGIN_PATH,
      query: to.path === ROOT_PATH ? {} : { redirect: to.fullPath },
    };
  }

  if (to.path === ROOT_PATH) {
    return fallbackPath;
  }

  // 首次直接打开动态详情地址时，先用刚注册的路由重新解析再做权限判断。
  if (routesChanged || isNotFoundMatch(to)) {
    const rematchedRoute = router.resolve(to.fullPath);
    if (hasRouteAccess(rematchedRoute, userStore.menus, userStore)) {
      return to.fullPath;
    }
  }

  if (hasRouteAccess(to, userStore.menus, userStore)) {
    return true;
  }

  return fallbackPath !== to.path ? fallbackPath : true;
});

window.addEventListener("moseycat:unauthorized", () => {
  if (handlingUnauthorized) {
    return;
  }

  handlingUnauthorized = true;
  const userStore = useUserStore();
  const tabsStore = useTabsStore();
  const currentRoute = router.currentRoute.value;

  userStore.clearSession();
  tabsStore.resetTabs();
  resetDynamicMenuRoutes();

  if (currentRoute.path !== LOGIN_PATH) {
    router
      .replace({
        path: LOGIN_PATH,
        query: {
          redirect: currentRoute.fullPath,
        },
      })
      .catch(() => undefined)
      .finally(() => {
        handlingUnauthorized = false;
      });
    return;
  }

  handlingUnauthorized = false;
});

export default router;
